import * as React from "react";
import { useApi } from "../containers/ApiContainer";
import { useIdentity } from "../containers/IdentityContainer";
import { usePrevious } from "react-use";
import { v4 as uuidv4 } from "uuid";
import type { SupportedLanguage } from '../i18n/config';

export type UserPreferences = {
  id: string;
  user_id: string;
  preferences: Preferences;
};
export type Preferences = {
  theme?: 'light' | 'dark';
  language?: SupportedLanguage;
  hideLockReminderAlert?: boolean;
  forceDisplayTailNumber?: boolean;
  showMeasurements?: boolean;
  [key: string]: any;
};

export const useUserPreferences = () => {
  const { postgrest } = useApi();
  const { airplxIdentity } = useIdentity();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [userPreferences, setUserPreferences] = React.useState<
    UserPreferences
  >();
  const previousUserPreferences = usePrevious(userPreferences);
  React.useEffect(() => {
    (async () => {
      if (!airplxIdentity?.userid) {
        setLoading(false);
        return;
      }
      const { data } = await postgrest
        .from("user_preferences")
        .select()
        .single();
      setUserPreferences({
        ...data,
        id: data?.id ?? uuidv4(),
        user_id: data?.user_id ?? airplxIdentity?.userid,
        preferences: data?.preferences ?? {},
      });
      setLoading(false);
    })();
  }, []);

  React.useEffect(() => {
    if (!userPreferences || !previousUserPreferences) {
      return;
    }
    (async () => {
      await postgrest.from("user_preferences").upsert({
        ...userPreferences,
      });
    })();
  }, [userPreferences]);

  const setPreferences = React.useCallback(
    (preferences: Preferences | ((prev: Preferences) => Preferences)) => {
      setUserPreferences((current) => {
        const newPreferences = typeof preferences === 'function'
          ? preferences(current?.preferences ?? {})
          : preferences;

        if (!current) {
          // If current is undefined, create a new UserPreferences with temporary values
          // These will be replaced when the actual data loads from the database
          return {
            id: '',
            user_id: '',
            preferences: newPreferences,
          } as UserPreferences;
        }

        return {
          ...current,
          preferences: newPreferences,
        };
      });
    },
    []
  );

  return {
    loading,
    userPreferences,
    setUserPreferences,
    preferences: userPreferences?.preferences ?? {},
    setPreferences,
  };
};
